<script>
import {useRoute} from "vue-router";
import {useCampaignStore} from "@/javascript/stores/campaign";
import Index from "@/javascript/views/components/dashboard/Index.vue";
import Show from "@/javascript/views/components/dashboard/Show.vue";
import NewCampaignModal from "@/javascript/views/clientDashboard/campaigns/NewCampaignModal.vue";
import {useAnalyticEventStore} from "@/javascript/stores/analyticEvents";
import HeaderBar from "@/javascript/views/components/HeaderBar.vue";
import TabsComponent from "@/javascript/views/components/dashboard/Tabs.vue";
import IntervalToggle from "@/javascript/views/components/overview/IntervalToggle.vue";
import DataCard from "@/javascript/views/components/overview/DataCard.vue";
import {useOverviewStore} from "@/javascript/stores/overview";
import BaseLineChart from "@/javascript/views/components/charts/BaseLineChart.vue";
import DonationPaymentChart from "@/javascript/views/components/DonationPaymentChart.vue";
import {ApplicationHelper} from "@/javascript/helpers/ApplicationHelper";
import BaseDataTable from "@/javascript/views/components/charts/BaseDataTable.vue";
import DoughnutChartDataCard from "@/javascript/views/components/charts/DoughnutChartDataCard.vue";

export default {
  name: "Campaign",
  components: {
    DoughnutChartDataCard,
    BaseDataTable,
    DonationPaymentChart,
    BaseLineChart,
    DataCard,
    HeaderBar,
    Index,
    Show,
    NewCampaignModal,
    TabsComponent,
    IntervalToggle
  },
  setup() {
    const route = useRoute();
    const campaignStore = useCampaignStore();
    const analyticEventStore = useAnalyticEventStore();
    const overviewStore = useOverviewStore();

    const toggleNewModal = () => {
      campaignStore.showNewModal = !campaignStore.showNewModal;
      analyticEventStore.logEvent({
        action: `new-campaign-modal-open`
      });
    };

    const handleIntervalChange = (newInterval) => {
      analyticEventStore.logEvent({
        action: `overview-change-interval`,
        metadata: {
          fromInterval: campaignStore.currentInterval,
          toInterval: newInterval
        }
      });
      campaignStore.currentInterval = newInterval;
      campaignStore.loadDataWithIntervals();
    };

    return {
      route,
      campaignStore,
      analyticEventStore,
      toggleNewModal,
      handleIntervalChange
    };
  },
  data() {
    return {
      // For the list (index) view:
      hasTabs: true,
      activeTab: "campaigns-tab",
      tabs: [
        {id: "campaigns-tab", label: "Campaigns"},
        // { id: "index-analytics", label: "Dashboard" },
      ],
      // For the show (detail) view:
      activeShowTab: "details",
      showTabs: [
        {id: "details", label: "Details"},
        // {id: "view-analytics", label: "Analytics"},
      ],
    };
  },
  computed: {
    campaignId() {
      return this.route.params.id;
    },
    canCreate() {
      return this.campaignStore.permissions && this.campaignStore.permissions.create;
    },
    topDonorsData() {
      const labels = ['Name', 'Amount'];
      // Only proceed if topDonors is loaded and defined
      if (this.campaignStore.topDonorsLoaded && this.campaignStore.topDonors) {
        let rows = [];
        // If topDonors is an array, use it directly; otherwise, check if it has a 'data' property.
        let donors = [];
        if (Array.isArray(this.campaignStore.topDonors)) {
          donors = this.campaignStore.topDonors;
        } else if (this.campaignStore.topDonors.data) {
          donors = this.campaignStore.topDonors.data;
        }
        donors.forEach((donor) => {
          rows.push({
            'Name': donor.first_name + " " + donor.last_name,
            'Amount': this.formattedMoney(donor.donations_sum)
          });
        });
        return {labels, rows};
      } else {
        return {labels, rows: []};
      }
    },
    dateRangeMessage() {
      const intervalMessages = {
        weekly: "Week to Date",
        monthly: "Month to Date",
        yearly: "Fiscal Year to Date"
      };
      return intervalMessages[this.currentInterval] || "";
    },
    donationTypeChartData() {
      // Chart Colors by label, add more if needed, gray as default
      const chartColours = {
        "One Time": "#10be5a",
        "Recurring": "#3a4473"
      };

      if (!this.campaignStore.donationTypeDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: ['#d3d3d3'],
              data: [1]
            }
          ]
        };
      }
      if (this.campaignStore.donationTypeDataLoaded
        && Object.keys(this.campaignStore.donationTypeData).length > 0) {
        let labels = Object.keys(this.campaignStore.donationTypeData).map((label) => {
          return label.split('_').map((word) => {
            //remove word donation from label ie -> "One Time", "Recurring"
            if (word === 'donation') return '';
            return word.charAt(0).toUpperCase() + word.slice(1);
          }).join(' ');
        });

        return {
          labels: labels,
          datasets: [
            {
              backgroundColor: function (context) {
                // Function to ensure chart colors are always correct for the label
                const label = context.chart.data.labels[context.dataIndex]?.trim();
                if (label && chartColours[label]) {
                  return chartColours[label];
                } else {
                  return "gray";
                }
              },
              data: Object.values(this.campaignStore.donationTypeData)
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },
    /** Formatted percentage data for donation frequencies
     * format -> {
     *   "One Time": "70%",
     *   "Recurring" "30%"
     * }
     * **/
    donationFrequencyPercents() {
      if (!this.campaignStore.donationTypeDataLoaded) {
        return {
          "One Time": null,
          "Recurring": null
        };
      }
      let oneTime = this.campaignStore.donationTypeData['one_time_donation'] || 0;
      let recurring = this.campaignStore.donationTypeData['recurring_donation'] || 0;
      let total = oneTime + recurring;

      if (!total) return "";

      let oneTimePercent = ((oneTime / total) * 100).toFixed(0);
      let recurringPercent = 100 - oneTimePercent;

      return {
        "One Time": `${oneTimePercent}%`,
        "Recurring": `${recurringPercent}%`
      };
    },
    donationProcessorTypeChartData() {
      if (!this.campaignStore.processorTypeDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: ['#d3d3d3'],
              data: [1]
            }
          ]
        };
      }
      if (this.campaignStore.processorTypeDataLoaded
        && Object.keys(this.campaignStore.processorTypeData).length > 0) {
        let colors = ['#10be5a', '#3a4473'];

        let labels = Object.keys(this.campaignStore.processorTypeData).map((label) => {
          if (label.toLowerCase().includes("offline")) {
            return "Offline";
          }
          return "Online";
        });

        if (labels.length === 1 && labels[0].includes('Offline')) {
          /// Want the chart to be blue if 100% offline donations
          colors = ['#3a4473'];
        }
        return {
          labels: labels,
          datasets: [
            {
              backgroundColor: colors,
              data: Object.values(this.campaignStore.processorTypeData)
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },
    /** Formatted percentage data for donation types
     * format -> {
     *   "Online": "70%",
     *   "Offline" "30%"
     * }
     * **/
    donationTypePercents() {
      if (!this.campaignStore.processorTypeDataLoaded) {
        return {
          "Online": null,
          "Offline": null
        };
      }
      let offline = this.campaignStore.processorTypeData['Processor::OfflineProcessor'] || 0;
      let online = this.campaignStore.processorTypeData['Processor::StripeProcessor'] || 0;
      let total = offline + online;
      if (!total) return "";

      let onlinePercent = ((online / total) * 100).toFixed(0);
      let offlinePercent = 100 - onlinePercent;

      return {
        "Online": `${onlinePercent}%`,
        "Offline": `${offlinePercent}%`
      };
    },
    donationsByFundChartData() {
      if (!this.campaignStore.donationsByFundDataLoaded) {
        return {
          labels: [''],
          datasets: [
            {
              backgroundColor: ['#D3D3D3'],
              data: [1]
            }
          ]
        };
      }
      if (this.campaignStore.donationsByFundDataLoaded
        && Object.keys(this.campaignStore.donationsByFundData).length > 0) {
        let labels = Object.keys(this.campaignStore.donationsByFundData).map((label) => {
          return label;
        });
        return {
          labels: labels,
          datasets: [
            {
              backgroundColor: ['#10be5a', '#3a4473', '#00bdff', '#9500ff', '#D3D3D3'],
              data: Object.values(this.campaignStore.donationsByFundData)
            }
          ]
        };
      } else {
        return {
          labels: [''],
          datasets: []
        };
      }
    },
    /** Formatted percentage data for donations by fund
     * Returning only highest fund and 'other'
     * format -> {
     *   "Fund 1": "43%",
     *   "Other": "57%"
     * }
     * **/
    donationByFundPercentData() {
      try {
        if (!this.campaignStore.donationsByFundDataLoaded) return "";

        const donationData = this.campaignStore.donationsByFundData;

        // Add up the total counts from the returned object
        const total = Object.values(donationData).reduce((sum, count) => sum + count, 0);

        // Get the entry with the highest value, we're only displaying the fund with the highest % donations
        let maxValue = 0;
        let maxKey = null;
        for (const key in donationData) {
          if (key.toLowerCase() === 'other') break;
          if (!maxKey || donationData[key] > maxValue) {
            maxKey = key;
            maxValue = donationData[key];
          }
        }

        if (!maxKey || total === 0) return {}; // Don't divide by 0!

        let highestPercent = Math.round((maxValue / total) * 100);
        let data = {};
        data[maxKey] = `${highestPercent}%`;
        data['Other'] = `${100 - highestPercent}%`;
        return data;
      } catch (e) {
        console.error('error parsing fund data', e);
        return {};
      }
    },
  },
  watch: {
    'campaignStore.organizationId'() {
      this.campaignStore.loadData();
    },
    campaignId(newId, oldId) {
      if (newId !== oldId) {
        this.campaignStore.clearData();
        if (newId) {
          this.campaignStore.getCampaign(newId).then(() => {
            this.campaignStore.loadDataWithIntervals();
          });
        } else {
          this.campaignStore.currentRecord = {};
          this.campaignStore.getCampaigns();
          this.campaignStore.getFunds();
        }
      }
    },
  },
  mounted() {
    if (this.campaignId) {
      this.campaignStore.getCampaign(this.campaignId).then(() => {
        this.campaignStore.loadDataWithIntervals();
      });
    } else {
      this.campaignStore.getCampaigns();
      this.campaignStore.getFunds();
    }
  },
  methods: {
    formattedMoney(val) {
      return ApplicationHelper.formatMoney(val);
    },
  }
};
</script>

<template>
  <div>
    <!-- If a campaign is selected, show the detail view with tabs -->
    <div v-if="campaignId && campaignStore.currentRecord">
      <div class="mt-2 pt-2 w-full p-4 mx-auto">
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <tabs-component
            v-model="activeShowTab"
            :tabs="showTabs"
          >
            <!-- Details tab: Render the Show component -->
            <template #details>
              <show
                :model-store="campaignStore"
                editable
              />
            </template>
            <!-- Analytics tab: includes IntervalToggle above placeholder DataCards -->
            <template #view-analytics>
              <div class="p-4">
                <!-- Interval Toggle component -->
                <div class="flex justify-end">
                  <interval-toggle @interval-change="handleIntervalChange" />
                </div>
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <DataCard
                    id="campaign-gifts-line-chart-data-card"
                    class="sm:col-span-2"
                    title="Gifts"
                    :additional-text="campaignStore.donationDataDateRange"
                    left-sidebar
                    title-icon="hand-heart"
                    icon-color="#10be5a"
                    :left-sidebar-data="[
                      { label: 'Total Gifts', value: campaignStore.periodDonationCount },
                      { label: 'Total Dollars', value: formattedMoney(campaignStore.periodDonationSum) }
                    ]"
                    :loaded="campaignStore.paymentDataLoaded"
                  >
                    <donation-payment-chart class="min-h-[300px]">
                      <template #donation-chart>
                        <BaseLineChart :data="campaignStore.donationProjectionChartData" />
                      </template>
                      <template #payment-chart>
                        <BaseLineChart
                          :data="campaignStore.paymentChartData"
                          y-prefix="$"
                        />
                      </template>
                    </donation-payment-chart>
                  </DataCard>

                  <!--                  <DataCard-->
                  <!--                    id="overview-top-donors-data-card"-->
                  <!--                    title="Top Donors"-->
                  <!--                    :additional-text="dateRangeMessage"-->
                  <!--                    title-icon="award"-->
                  <!--                    icon-color="#10be5a"-->
                  <!--                    loaded-->
                  <!--                  >-->
                  <!--                    <base-data-table-->
                  <!--                      v-bind="topDonorsData"-->
                  <!--                      :loading="!campaignStore.topDonorsLoaded"-->
                  <!--                      :highlighted-column="1"-->
                  <!--                      no-data-message="No donations have been made in this time period."-->
                  <!--                      :loading-rows="5"-->
                  <!--                    />-->
                  <!--                  </DataCard>-->
                  <div class="w-full">
                    <doughnut-chart-data-card
                      title="Gift Frequency"
                      :subtitle="dateRangeMessage"
                      :chart-data="donationTypeChartData"
                      :percent-data="donationFrequencyPercents"
                    />
                  </div>

                  <div class="w-full">
                    <doughnut-chart-data-card
                      title="Gift Type"
                      :subtitle="dateRangeMessage"
                      :chart-data="donationProcessorTypeChartData"
                      :percent-data="donationTypePercents"
                    />
                  </div>
                </div>
              </div>
            </template>
          </tabs-component>
        </div>
      </div>
    </div>
    <!-- Index view: when no campaign is selected -->
    <div v-else>
      <!-- Header Bar with title and New Campaign button -->
      <div class="px-4">
        <header-bar
          title="Campaigns"
        >
          <template #buttons>
            <button
              v-if="canCreate"
              class="btn-outline"
              @click="toggleNewModal"
            >
              New Campaign
            </button>
          </template>
        </header-bar>
      </div>
      <!-- Index view wrapped in TabsComponent with analytics tab -->
      <div
        v-if="hasTabs"
        class="mt-2 pt-2 w-full p-4 mx-auto"
      >
        <div class="breakpoint-container relative rounded-xl overflow-hidden border border-gray-300 text-xs">
          <tabs-component
            v-model="activeTab"
            :tabs="tabs"
          >
            <template #campaigns-tab>
              <index :model-store="campaignStore" />
            </template>
            <template #index-analytics>
              <div class="p-4">
                <!-- Interval Toggle at the top -->
                <!--                <div class="flex justify-end">-->
                <!--                  <interval-toggle @interval-change="handleIntervalChange" />-->
                <!--                </div>-->
                <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                  <doughnut-chart-data-card
                    title="Top Campaigns"
                    :subtitle="dateRangeMessage"
                    :chart-data="donationsByFundChartData"
                    :percent-data="donationByFundPercentData"
                    format-as-money
                  />
                </div>
              </div>
            </template>
          </tabs-component>
        </div>
      </div>
      <div
        v-else
        class="breakpoint-container mt-4"
      >
        <index :model-store="campaignStore" />
      </div>
      <!-- New Campaign Modal -->
      <new-campaign-modal v-if="campaignStore.showNewModal" />
    </div>
  </div>
</template>
